/**
 * @file section-edit.ts
 * @description Чистые правки секции в кабинете (phase19.16 Р2;
 * uiux-design-r2 §2, §4, §7; SB64): поиск элемента по id, вставка нового
 * с уникальным id, дублирование, удаление, перенос (внутри колонки и между
 * колонками), смена числа колонок с переносом элементов в последнюю, правка
 * текста по пути из канвы, поиск второго главного заголовка на странице.
 *
 * SB120 этап 1 (v3): число колонок и доли из 12 (`setColumns`), фото
 * фоном с высотой «По фото» / «По кадру телефона 4:5» для нового фото у
 * блока «по содержимому» (вопрос 7, решение 2026-10-18).
 *
 * Функции не мутируют вход — возвращают новый payload (форма держит его в
 * состоянии и сравнивает с сохранённым).
 */

import {
  defaultElement,
  defaultMediaBackground,
  equalShares,
  isValidPhoneOrder,
  photoBackgroundHeight,
  SITE_LIMITS,
  type SectionElement,
  type SectionElementKind,
  type SectionLook,
  type SectionMediaBg,
  type SectionPayload,
} from "@chaiberi/shared";

/** Где элемент: колонка и место в ней. */
export interface ElementLocation {
  col: number;
  row: number;
  element: SectionElement;
}

/** Все id элементов секции. */
export function elementIds(payload: SectionPayload): Set<string> {
  const ids = new Set<string>();
  for (const column of payload.columns.items) for (const el of column.elements) ids.add(el.id);
  return ids;
}

/** Свободный id для нового элемента: `button-1`, `button-2`… */
export function nextElementId(payload: SectionPayload, kind: SectionElementKind): string {
  const taken = elementIds(payload);
  let n = 1;
  while (taken.has(`${kind}-${n}`)) n += 1;
  return `${kind}-${n}`;
}

export function findElement(payload: SectionPayload, id: string): ElementLocation | null {
  for (const [col, column] of payload.columns.items.entries()) {
    const row = column.elements.findIndex((el) => el.id === id);
    const element = column.elements[row];
    if (element) return { col, row, element };
  }
  return null;
}

/** Новый payload с изменённым списком элементов колонки. */
function withColumn(payload: SectionPayload, col: number, elements: SectionElement[]): SectionPayload {
  return {
    ...payload,
    columns: {
      ...payload.columns,
      items: payload.columns.items.map((column, i) => (i === col ? { ...column, elements } : column)),
    },
  };
}

export function replaceElement(payload: SectionPayload, id: string, next: SectionElement): SectionPayload {
  const at = findElement(payload, id);
  if (!at) return payload;
  const column = payload.columns.items[at.col]?.elements ?? [];
  return withColumn(payload, at.col, column.map((el, i) => (i === at.row ? next : el)));
}

/** Колонка заполнена (SB64: не больше 10 элементов). */
export function columnFull(payload: SectionPayload, col: number): boolean {
  return (payload.columns.items[col]?.elements.length ?? 0) >= SITE_LIMITS.elementsPerColumn;
}

/** Вставить новый элемент вида `kind` в конец колонки; `null` — колонка заполнена. */
export function insertElement(
  payload: SectionPayload,
  col: number,
  kind: SectionElementKind,
): { payload: SectionPayload; id: string } | null {
  const column = payload.columns.items[col];
  if (!column || columnFull(payload, col)) return null;
  const id = nextElementId(payload, kind);
  return { payload: withColumn(payload, col, [...column.elements, defaultElement(kind, id)]), id };
}

export function removeElement(payload: SectionPayload, id: string): SectionPayload {
  const at = findElement(payload, id);
  if (!at) return payload;
  const column = payload.columns.items[at.col]?.elements ?? [];
  return withColumn(payload, at.col, column.filter((el) => el.id !== id));
}

/** Копия элемента сразу ниже; `null` — колонка заполнена. */
export function duplicateElement(payload: SectionPayload, id: string): { payload: SectionPayload; id: string } | null {
  const at = findElement(payload, id);
  if (!at || columnFull(payload, at.col)) return null;
  const copyId = nextElementId(payload, at.element.kind);
  const copy = { ...structuredClone(at.element), id: copyId };
  const column = [...(payload.columns.items[at.col]?.elements ?? [])];
  column.splice(at.row + 1, 0, copy);
  return { payload: withColumn(payload, at.col, column), id: copyId };
}

/** Выше / ниже внутри колонки. */
export function moveElementBy(payload: SectionPayload, id: string, dir: -1 | 1): SectionPayload {
  const at = findElement(payload, id);
  if (!at) return payload;
  const column = [...(payload.columns.items[at.col]?.elements ?? [])];
  const to = at.row + dir;
  if (to < 0 || to >= column.length) return payload;
  [column[at.row], column[to]] = [column[to] as SectionElement, column[at.row] as SectionElement];
  return withColumn(payload, at.col, column);
}

/** Перенести элемент в колонку `col` на место `index` (перетаскивание, в том числе между колонками). */
export function moveElementTo(payload: SectionPayload, id: string, col: number, index: number): SectionPayload {
  const at = findElement(payload, id);
  if (!at || !payload.columns.items[col]) return payload;
  if (at.col !== col && columnFull(payload, col)) return payload;
  const without = removeElement(payload, id);
  const target = [...(without.columns.items[col]?.elements ?? [])];
  target.splice(Math.max(0, Math.min(index, target.length)), 0, at.element);
  return withColumn(without, col, target);
}

/**
 * Новые доли колонок (их число = число колонок). Колонок стало меньше —
 * элементы убранных колонок переносятся в последнюю оставшуюся (`moved` —
 * сколько, для подтверждения); больше — добавляются пустые. Рисунки,
 * привязанные к убранной колонке, переходят к секции; порядок на телефоне,
 * который перестал быть перестановкой 1..N, снимается («по порядку»).
 */
export function setColumns(payload: SectionPayload, shares: readonly number[]): { payload: SectionPayload; moved: number } {
  const count = shares.length;
  const items = payload.columns.items.map((c) => ({ ...c, elements: [...c.elements] }));
  let moved = 0;
  while (items.length > count) {
    const removed = items.pop();
    const last = items[items.length - 1];
    if (removed && last) {
      moved += removed.elements.length;
      last.elements.push(...removed.elements);
    }
  }
  while (items.length < count) items.push({ elements: [] });
  const columns = { ...payload.columns, shares: [...shares], items };
  if (columns.phoneOrder && !isValidPhoneOrder(columns.phoneOrder, count)) delete columns.phoneOrder;
  return {
    moved,
    payload: {
      ...payload,
      columns,
      look: {
        ...payload.look,
        decor: payload.look.decor.map((d) => (d.column !== null && d.column > count ? { ...d, column: null } : d)),
      },
    },
  };
}

/** Равные доли для числа колонок — «пустой блок N колонок». */
export function sharesForColumns(count: number): number[] {
  return equalShares(count);
}

/** Путь поля, которое канва умеет править на месте: `header.title`, `columns.items.0.elements.2.text`. */
const EDITABLE_PATH = /^(?:header\.(?:title|eyebrow)|columns\.items\.(\d+)\.elements\.(\d+)\.(text|label))$/;

/**
 * Правка текста с канвы (`section-edit { path, value }`). Незнакомый путь
 * или не та форма — `null` (сообщение игнорируется, а не ломает блок).
 */
export function applyCanvasText(payload: SectionPayload, path: string, value: string): SectionPayload | null {
  const match = EDITABLE_PATH.exec(path);
  if (!match) return null;
  if (path.startsWith("header.")) {
    if (!payload.header) return null;
    const key = path === "header.title" ? "title" : "eyebrow";
    return { ...payload, header: { ...payload.header, [key]: value } };
  }
  const col = Number(match[1]);
  const row = Number(match[2]);
  const field = match[3];
  const element = payload.columns.items[col]?.elements[row];
  if (!element || !(field && field in element)) return null;
  return replaceElement(payload, element.id, { ...element, [field]: value } as SectionElement);
}

/** Путь элемента из канвы (`element-select { path }`) → id элемента. */
export function elementIdAtPath(payload: SectionPayload, path: string): string | null {
  const match = /^columns\.items\.(\d+)\.elements\.(\d+)/.exec(path);
  if (!match) return null;
  return payload.columns.items[Number(match[1])]?.elements[Number(match[2])]?.id ?? null;
}

/** Есть ли в секции главный заголовок (H1): в шапке раздела или элементом. */
export function hasH1(payload: SectionPayload): boolean {
  if (payload.header?.level === 1) return true;
  return payload.columns.items.some((c) => c.elements.some((el) => el.kind === "heading" && el.level === 1));
}

/** Сколько всего элементов в секции. */
export function elementCount(payload: SectionPayload): number {
  return payload.columns.items.reduce((sum, c) => sum + c.elements.length, 0);
}

/** Свободный id пункта списка внутри элемента: `a1`, `s2`, `r3`… */
export function nextItemId(items: ReadonlyArray<{ id: string }>, prefix: string): string {
  let n = 1;
  while (items.some((i) => i.id === `${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
}

/** Затемнение нового фото-фона секции (20 %) — и «по умолчанию» у поля «Затемнение» (SB120). */
export const MEDIA_BG_DIM = 0.2;

/** Новое фото фона: фокус в центре, без растворения, светлый текст, затемнение 20 %. */
export function newMediaBg(mediaId: string): SectionMediaBg {
  return { ...defaultMediaBackground(mediaId), dim: MEDIA_BG_DIM };
}

/**
 * Выбрано фото фоном. Было фото — меняется только файл (кадр и настройки
 * остаются). Нового фото у блока «по содержимому» без своей высоты телефона —
 * высота «По фото» на ПК и «По кадру телефона 4:5» (вопрос 7, 2026-10-18;
 * `photoBackgroundHeight` из shared); у остальных высота не меняется.
 */
export function withPhotoBackground(look: SectionLook, mediaId: string): SectionLook {
  if (look.bg.kind === "media") return { ...look, bg: { ...look.bg, mediaId } };
  const next: SectionLook = { ...look, bg: newMediaBg(mediaId) };
  return look.height.kind === "content" && !look.heightPhone ? { ...next, ...photoBackgroundHeight() } : next;
}

/**
 * Сравнимый вид JSON: ключи по алфавиту, `undefined` отброшен, пустой объект
 * — `null` (фон страницы «как у сайта» с сервера приходит `null`, а форма
 * может держать `{}` или поля в другом порядке).
 */
export function canonicalJson(value: unknown): string {
  const norm = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(norm);
    if (v && typeof v === "object") {
      const entries = Object.entries(v as Record<string, unknown>)
        .filter(([, x]) => x !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, x]) => [k, norm(x)] as const);
      return Object.fromEntries(entries);
    }
    return v;
  };
  const n = norm(value ?? null);
  return n && typeof n === "object" && !Array.isArray(n) && Object.keys(n).length === 0 ? "null" : JSON.stringify(n);
}
